// @tg-seq: 11200
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VOICE RECORDING OVERLAY — Universal Voice-to-Text Transcription
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Modern Web Audio API implementation with LLM transcription integration.
 * Floating overlay pattern (accessible from any screen).
 *
 * Features:
 *   - Web Audio API recording (MediaRecorder)
 *   - Real-time visualization (audio levels)
 *   - Cloud transcription (Azure Speech/OpenAI Whisper)
 *   - Confidence scoring
 *   - Context-aware (links to active screen/entity)
 *   - Integration with Context Formation framework
 *
 * Pattern: Floating FAB → Recording Modal → Transcription Result
 * Database: LLM.Voice_Transcription_Queue (via /api/voice-transcription)
 *
 * Created: 2026-08-22 (Refactored from deleted prototype)
 * Architecture: Redux auth + MUI + Modern Web APIs
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fab,
    IconButton,
    LinearProgress,
    Paper,
    TextField,
    Autocomplete,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Mic,
    Stop,
    Close,
    Send,
    Replay,
    GraphicEq,
    CheckCircle,
    Warning,
    VolumeUp,
    PlayArrow,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../../utils/useAuth';
import { apiRequest } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';

interface VoiceRecordingOverlayProps {
    /** Optional context for linking recording to specific entity */
    context?: {
        sourceChannel?: string; // 'TaskComposer', 'Meeting', 'ChatPanel', 'ConsultantInterview'
        sourceReference?: string; // Entity UUID or reference ID
        existing_transcription_uuid?: string; // UUID for appending to existing transcript
    };
    /** Callback when transcription completes (includes UUID for editing).
     * blob_uuid (2026-10-07) is the TEL.Blob_Storage_Registry row created for
     * the uploaded audio (when one was registered) - callers that create a
     * TEL.Voice_Message should thread this into its Blob_UUID column. */
    onTranscriptionComplete?: (
        transcription: string,
        confidence: number,
        transcription_uuid: string,
        blob_uuid?: string,
    ) => void;
    /** Maximum recording duration in seconds (default: 300 = 5 minutes) */
    maxDurationSeconds?: number;
    /**
     * Controlled-open support (e.g. hosted by Phone Tool to launch recording
     * alongside its own Call Entry Notes dialog). Omit both to keep the
     * component's own internal FAB-triggered open state (standalone usage).
     */
    controlledOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Hides the floating FAB trigger — used when a host component owns the
     * open/close lifecycle via controlledOpen/onOpenChange instead. */
    hideTrigger?: boolean;
}

interface RecordingState {
    isRecording: boolean;
    isPaused: boolean;
    isProcessing: boolean;
    duration: number; // seconds
    audioBlob: Blob | null;
    audioUrl: string | null;
}

interface TranscriptionResult {
    text: string;
    confidence: number;
    transcription_uuid: string;
    blob_uuid?: string;
    durationSeconds: number;
    sourceChannel?: string;
    status: string;
}

const Self: React.FC<VoiceRecordingOverlayProps> = ({
    context,
    onTranscriptionComplete,
    maxDurationSeconds = 300,
    controlledOpen,
    onOpenChange,
    hideTrigger = false,
}) => {
    const { pfUser, activeOrganizationUUID } = useAuth();

    // This overlay already serves a distinct, pre-existing "agent/architect
    // ideation" dictation use case (businessSubject/subjectTransaction/data use
    // tagging below) — Speed Dial Voicemail reuses the same record→transcribe
    // engine but doesn't need any of that tagging UI, so it's hidden via this
    // context switch rather than forking a second component.
    const isVoicemailContext = context?.sourceChannel === 'Speed Dial Voicemail';

    // Mode toggle: 'stt' (Speech-to-Text) or 'tts' (Text-to-Speech)
    type VoiceMode = 'stt' | 'tts';
    const [voiceMode, setVoiceMode] = useState<VoiceMode>('stt');

    // TTS-specific state
    const [ttsText, setTtsText] = useState('');
    const [selectedVoice, setSelectedVoice] = useState('en-US-AriaNeural');
    const [ttsAudioUrl, setTtsAudioUrl] = useState<string | null>(null);
    const [ttsLoading, setTtsLoading] = useState(false);
    const [ttsError, setTtsError] = useState<string | null>(null);

    const [open, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const setOpen = (value: boolean) => {
        if (isControlled) {
            onOpenChange?.(value);
        } else {
            setInternalOpen(value);
        }
    };
    const effectiveOpen = isControlled ? !!controlledOpen : open;
    const [businessSubject, setBusinessSubject] = useState(''); // NEW: Business context
    const [subjectTransaction, setSubjectTransaction] = useState(''); // NEW: Transaction type
    const [dataUseOptions, setDataUseOptions] = useState<
        Array<{ data_use_id: number; data_use: string }>
    >([]);
    const [selectedDataUses, setSelectedDataUses] = useState<
        Array<{ data_use_id: number; data_use: string }>
    >([]); // Multi-select
    const [agenticFunctionOptions, setAgenticFunctionOptions] = useState<
        Array<{
            agentic_function_id: number;
            function_code: string;
            function_name: string;
            function_category: string;
            estimated_avg_duration_minutes: number | null;
        }>
    >([]);
    const [selectedAgenticFunction, setSelectedAgenticFunction] = useState<{
        agentic_function_id: number;
        function_code: string;
        function_name: string;
        function_category: string;
        estimated_avg_duration_minutes: number | null;
    } | null>(null);
    const [recordingState, setRecordingState] = useState<RecordingState>({
        isRecording: false,
        isPaused: false,
        isProcessing: false,
        duration: 0,
        audioBlob: null,
        audioUrl: null,
    });
    const [transcription, setTranscription] = useState<TranscriptionResult | null>(null);
    const [accumulatedTranscriptions, setAccumulatedTranscriptions] = useState<string[]>([]); // Append mode
    const [error, setError] = useState<string | null>(null);
    const [audioLevel, setAudioLevel] = useState<number>(0);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopRecording();
            if (timerRef.current) clearInterval(timerRef.current);
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            if (audioContextRef.current) audioContextRef.current.close();
        };
    }, []);

    // Load Data Use lookup options when dialog opens
    useEffect(() => {
        if (open && dataUseOptions.length === 0) {
            // TODO: Replace with actual API call to fetch TGAUI.fk_Data_Use
            // For now, using static data from user's message
            const staticDataUses = [
                { data_use_id: 0, data_use: 'Unidentified' },
                { data_use_id: 1, data_use: 'Platform Global Master' },
                { data_use_id: 6, data_use: 'PfUser Profile' },
                { data_use_id: 11, data_use: 'Tenant Master Data' },
                { data_use_id: 14, data_use: 'Tenant Org Profile' },
                { data_use_id: 16, data_use: 'Tenant Org Transaction' },
                { data_use_id: 18, data_use: 'Tenant Org Task' },
                { data_use_id: 19, data_use: 'Tenant Org Metric' },
            ];
            setDataUseOptions(staticDataUses);
        }
    }, [open, dataUseOptions.length]);

    // Load Agentic Function options when dialog opens
    useEffect(() => {
        const loadAgenticFunctions = async () => {
            if (open && agenticFunctionOptions.length === 0) {
                try {
                    // ✅ CORRECT: Use apiRequest() from apiService (established architecture)
                    const response: any = await apiRequest(
                        '/api/voice-transcription-queue',
                        'POST',
                        { manage: 8 }, // GET_AGENTIC_FUNCTION_MASTER
                    );
                    if (response.success && response.data) {
                        setAgenticFunctionOptions(response.data);
                    }
                } catch (err) {
                    console.error('Failed to load agentic functions:', err);
                }
            }
        };

        loadAgenticFunctions();
    }, [open, agenticFunctionOptions.length]);

    // Audio level visualization
    const visualizeAudioLevel = () => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average volume
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setAudioLevel(average);

        animationFrameRef.current = requestAnimationFrame(visualizeAudioLevel);
    };

    const startRecording = async () => {
        setError(null);
        setTranscription(null);

        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Setup Web Audio API for visualization
            const audioContext = new AudioContext();
            const analyser = audioContext.createAnalyser();
            const source = audioContext.createMediaStreamSource(stream);
            source.connect(analyser);
            analyser.fftSize = 256;
            audioContextRef.current = audioContext;
            analyserRef.current = analyser;
            visualizeAudioLevel();

            // Setup MediaRecorder
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
            });

            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                const audioUrl = URL.createObjectURL(audioBlob);

                setRecordingState((prev) => ({
                    ...prev,
                    isRecording: false,
                    audioBlob,
                    audioUrl,
                }));

                // Stop audio visualization
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                setAudioLevel(0);

                // Auto-process transcription
                await processTranscription(audioBlob);
            };

            mediaRecorder.start();
            mediaRecorderRef.current = mediaRecorder;

            setRecordingState((prev) => ({
                ...prev,
                isRecording: true,
                duration: 0,
            }));

            // Start timer with auto-stop at max duration
            timerRef.current = setInterval(() => {
                setRecordingState((prev) => {
                    const newDuration = prev.duration + 1;
                    // Auto-stop at max duration
                    if (newDuration >= maxDurationSeconds) {
                        stopRecording();
                    }
                    return {
                        ...prev,
                        duration: newDuration,
                    };
                });
            }, 1000);
        } catch (err: any) {
            console.error('Failed to start recording:', err);
            setError('Microphone access denied. Please allow microphone permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && recordingState.isRecording) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
            mediaRecorderRef.current = null;
        }

        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
    };

    const processTranscription = async (audioBlob: Blob) => {
        if (!pfUser?.user_uuid) return;

        setRecordingState((prev) => ({ ...prev, isProcessing: true }));
        setError(null);

        try {
            // Create FormData with audio file
            const formData = new FormData();
            formData.append('audio_file', audioBlob, 'recording.webm');
            formData.append('submitted_by_pfuser_uuid', pfUser.user_uuid);
            // Convert 'ALL' to universal templates UUID
            formData.append(
                'organization_uuid',
                activeOrganizationUUID === 'ALL'
                    ? '00000000-0000-0000-0000-000000000000'
                    : activeOrganizationUUID || '',
            );
            formData.append('ttp_id', '0'); // 0=Production, 1=Test
            formData.append('audio_duration_seconds', recordingState.duration.toString());

            if (context?.sourceChannel) formData.append('source_channel', context.sourceChannel);
            if (context?.sourceReference)
                formData.append('source_reference', context.sourceReference);

            // Add business context fields
            if (businessSubject) formData.append('business_subject', businessSubject);
            if (subjectTransaction) formData.append('subject_transaction', subjectTransaction);
            if (selectedDataUses.length > 0) {
                // Send first selected Data Use ID (backend expects single INT)
                formData.append('data_use_id', selectedDataUses[0].data_use_id.toString());
            }
            if (selectedAgenticFunction) {
                formData.append(
                    'agentic_function_id',
                    selectedAgenticFunction.agentic_function_id.toString(),
                );
            }

            // Debug logging
            console.log('📤 Voice Recording - Sending to backend:', {
                businessSubject,
                subjectTransaction,
                selectedDataUses,
                selectedAgenticFunction,
                formDataKeys: Array.from(formData.keys()),
            });

            // Call backend API - Use /upload endpoint for file uploads
            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/api/voice-transcription-queue/upload`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                },
            );

            if (response.data.success && response.data.data.length > 0) {
                const result = response.data.data[0];
                const transcriptionResult: TranscriptionResult = {
                    text: result.raw_transcription,
                    confidence: result.transcription_confidence,
                    transcription_uuid: result.transcription_uuid,
                    blob_uuid: result.blob_uuid,
                    durationSeconds: result.audio_duration_seconds,
                    sourceChannel: result.source_channel,
                    status: result.status,
                };

                setTranscription(transcriptionResult);

                // Append to accumulated transcriptions
                setAccumulatedTranscriptions((prev) => [...prev, transcriptionResult.text]);

                // Callback to parent with accumulated text
                const fullTranscription = [
                    ...accumulatedTranscriptions,
                    transcriptionResult.text,
                ].join(' ');
                if (onTranscriptionComplete) {
                    onTranscriptionComplete(
                        fullTranscription,
                        transcriptionResult.confidence,
                        transcriptionResult.transcription_uuid,
                        transcriptionResult.blob_uuid,
                    );
                }
            } else {
                throw new Error('Transcription failed - no results returned');
            }
        } catch (err: any) {
            console.error('Transcription error:', err);
            setError(err.response?.data?.detail || 'Failed to transcribe audio. Please try again.');
        } finally {
            setRecordingState((prev) => ({ ...prev, isProcessing: false }));
        }
    };

    const resetRecording = () => {
        if (recordingState.audioUrl) {
            URL.revokeObjectURL(recordingState.audioUrl);
        }
        setRecordingState({
            isRecording: false,
            isPaused: false,
            isProcessing: false,
            duration: 0,
            audioBlob: null,
            audioUrl: null,
        });
        setTranscription(null);
        setError(null);
    };

    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const formatTimeRemaining = (seconds: number): string => {
        const remaining = Math.max(0, maxDurationSeconds - seconds);
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        return `${mins}:${String(secs).padStart(2, '0')} remaining`;
    };

    const getConfidenceColor = (confidence: number): 'success' | 'warning' | 'error' => {
        if (confidence >= 85) return 'success';
        if (confidence >= 70) return 'warning';
        return 'error';
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // TTS (Text-to-Speech) Functions
    // ═══════════════════════════════════════════════════════════════════════════

    const voiceOptions = [
        { value: 'en-US-AriaNeural', label: 'Aria (US Female)' },
        { value: 'en-US-GuyNeural', label: 'Guy (US Male)' },
        { value: 'en-US-JennyNeural', label: 'Jenny (US Female)' },
        { value: 'en-US-DavisNeural', label: 'Davis (US Male)' },
        { value: 'en-GB-RyanNeural', label: 'Ryan (UK Male)' },
        { value: 'en-GB-SoniaNeural', label: 'Sonia (UK Female)' },
    ];

    const generateSpeech = async () => {
        if (!ttsText.trim()) {
            setTtsError('Please enter text to synthesize');
            return;
        }

        setTtsLoading(true);
        setTtsError(null);

        // Clear previous audio
        if (ttsAudioUrl) {
            URL.revokeObjectURL(ttsAudioUrl);
            setTtsAudioUrl(null);
        }

        try {
            const response = await apiRequest<{
                success: boolean;
                audio_data?: string;
                error?: string;
            }>('/api/text-to-speech/', 'POST', {
                text: ttsText,
                voice_name: selectedVoice,
                audio_format: 'audio-24khz-48kbitrate-mono-mp3',
            });

            if (response.success && response.audio_data) {
                // Decode base64 to blob
                const audioBase64 = response.audio_data;
                const byteCharacters = atob(audioBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const audioBlob = new Blob([byteArray], { type: 'audio/mpeg' });
                const audioUrl = URL.createObjectURL(audioBlob);

                setTtsAudioUrl(audioUrl);
            } else {
                throw new Error('No audio data returned');
            }
        } catch (err: any) {
            console.error('TTS error:', err);
            setTtsError(
                err.response?.data?.error ||
                    err.response?.data?.detail ||
                    err.message ||
                    'Failed to generate speech',
            );
        } finally {
            setTtsLoading(false);
        }
    };

    // Cleanup TTS audio on unmount
    useEffect(() => {
        return () => {
            if (ttsAudioUrl) {
                URL.revokeObjectURL(ttsAudioUrl);
            }
        };
    }, [ttsAudioUrl]);

    return (
        <>
            {/* Floating Action Button */}
            {!hideTrigger && (
                <Tooltip title="Voice Interface (Speak & Listen)" placement="left">
                    <Fab
                        color="primary"
                        sx={{
                            position: 'fixed',
                            bottom: 24,
                            right: 24,
                            zIndex: 1300,
                        }}
                        onClick={() => setOpen(true)}
                    >
                        <Mic />
                    </Fab>
                </Tooltip>
            )}

            {/* Recording Modal */}
            <Dialog
                open={effectiveOpen}
                onClose={() => !recordingState.isRecording && setOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {voiceMode === 'stt' ? (
                                <Mic color="primary" />
                            ) : (
                                <VolumeUp color="primary" />
                            )}
                            <Typography variant="h6">
                                {voiceMode === 'stt' ? 'Voice Recording' : 'Text-to-Speech'}
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => !recordingState.isRecording && setOpen(false)}
                            disabled={recordingState.isRecording}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    {/* Mode Toggle */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button
                            fullWidth
                            variant={voiceMode === 'stt' ? 'contained' : 'outlined'}
                            startIcon={<Mic />}
                            onClick={() => setVoiceMode('stt')}
                            disabled={recordingState.isRecording}
                        >
                            Speak to AI
                        </Button>
                        <Button
                            fullWidth
                            variant={voiceMode === 'tts' ? 'contained' : 'outlined'}
                            startIcon={<VolumeUp />}
                            onClick={() => setVoiceMode('tts')}
                        >
                            Listen to AI
                        </Button>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {/* ═══════════════════════════════════════════════════════════════ */}
                    {/* STT MODE (Speech-to-Text) - EXISTING */}
                    {/* ═══════════════════════════════════════════════════════════════ */}
                    {voiceMode === 'stt' && (
                        <>
                            {/* Business Context Fields — ideation/dictation use cases only;
                                not relevant when capturing a Speed Dial voicemail. */}
                            {!isVoicemailContext && (
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Business Subject"
                                        value={businessSubject}
                                        onChange={(e) => setBusinessSubject(e.target.value)}
                                        placeholder="e.g., Credit Card Registry, Scale Ticket Capture"
                                        size="small"
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Subject Transaction"
                                        value={subjectTransaction}
                                        onChange={(e) => setSubjectTransaction(e.target.value)}
                                        placeholder="e.g., CRUD Operations, Mobile Data Entry"
                                        size="small"
                                        sx={{ mb: 1 }}
                                    />
                                    <Autocomplete
                                        multiple
                                        options={dataUseOptions}
                                        getOptionLabel={(option) => option.data_use}
                                        value={selectedDataUses}
                                        onChange={(_, newValue) => setSelectedDataUses(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Data Use"
                                                placeholder="Select data use categories"
                                                size="small"
                                            />
                                        )}
                                        renderValue={(value, getItemProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    label={option.data_use}
                                                    size="small"
                                                    {...getItemProps({ index })}
                                                />
                                            ))
                                        }
                                    />
                                    <Autocomplete
                                        options={agenticFunctionOptions}
                                        getOptionLabel={(option) =>
                                            `${option.function_code} - ${option.function_name}`
                                        }
                                        groupBy={(option) => option.function_category}
                                        value={selectedAgenticFunction}
                                        onChange={(_, newValue) =>
                                            setSelectedAgenticFunction(newValue)
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Agentic Function (Task Type)"
                                                placeholder="Select task type"
                                                size="small"
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props}>
                                                <Box>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 'medium',
                                                        }}
                                                    >
                                                        {option.function_code}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        {option.function_name}
                                                        {option.estimated_avg_duration_minutes && (
                                                            <>
                                                                {' '}
                                                                • ~
                                                                {
                                                                    option.estimated_avg_duration_minutes
                                                                }{' '}
                                                                min
                                                            </>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            )}

                            {/* Context Info */}
                            {context?.contextDescription && (
                                <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Context: {context.contextType}
                                    </Typography>
                                    <Typography variant="body2">
                                        {context.contextDescription}
                                    </Typography>
                                </Paper>
                            )}

                            {/* Recording Controls */}
                            <Card sx={{ mb: 2 }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    {/* Audio Level Visualization */}
                                    {recordingState.isRecording && (
                                        <Box sx={{ mb: 2 }}>
                                            <GraphicEq
                                                sx={{
                                                    fontSize: 64,
                                                    color: 'primary.main',
                                                    animation: 'pulse 1s ease-in-out infinite',
                                                    '@keyframes pulse': {
                                                        '0%, 100%': { opacity: 1 },
                                                        '50%': { opacity: 0.5 },
                                                    },
                                                }}
                                            />
                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min((audioLevel / 128) * 100, 100)}
                                                sx={{ mt: 1 }}
                                            />
                                        </Box>
                                    )}

                                    {/* Duration Timer */}
                                    <Typography
                                        variant="h4"
                                        sx={{ mb: 1, fontFamily: 'monospace' }}
                                    >
                                        {formatDuration(recordingState.duration)}
                                    </Typography>
                                    {/* Countdown Timer */}
                                    {recordingState.isRecording && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 2,
                                                display: 'block',
                                            }}
                                        >
                                            {formatTimeRemaining(recordingState.duration)}
                                        </Typography>
                                    )}

                                    {/* Recording State */}
                                    {recordingState.isRecording && (
                                        <Chip
                                            icon={<Mic />}
                                            label="Recording..."
                                            color="error"
                                            sx={{ mb: 2 }}
                                        />
                                    )}

                                    {recordingState.isProcessing && (
                                        <Box sx={{ mb: 2 }}>
                                            <CircularProgress size={24} sx={{ mr: 1 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Transcribing audio...
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Control Buttons */}
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                        {!recordingState.isRecording &&
                                            !recordingState.audioBlob && (
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    startIcon={<Mic />}
                                                    onClick={startRecording}
                                                    disabled={recordingState.isProcessing}
                                                >
                                                    Start Recording
                                                </Button>
                                            )}

                                        {recordingState.isRecording && (
                                            <Button
                                                variant="contained"
                                                size="large"
                                                color="error"
                                                startIcon={<Stop />}
                                                onClick={stopRecording}
                                            >
                                                Stop Recording
                                            </Button>
                                        )}

                                        {recordingState.audioBlob &&
                                            !recordingState.isProcessing && (
                                                <>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Replay />}
                                                        onClick={resetRecording}
                                                    >
                                                        New Recording
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        startIcon={<Mic />}
                                                        onClick={() => {
                                                            resetRecording();
                                                            setTimeout(() => startRecording(), 100);
                                                        }}
                                                    >
                                                        Add Recording
                                                    </Button>
                                                </>
                                            )}
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Error Display */}
                            {error && (
                                <Paper
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        bgcolor: 'error.light',
                                        color: 'error.contrastText',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Warning />
                                        <Typography variant="body2">{error}</Typography>
                                    </Box>
                                </Paper>
                            )}

                            {/* Transcription Result */}
                            {transcription && (
                                <Paper
                                    sx={{
                                        p: 2,
                                        bgcolor: 'success.light',
                                        border: 2,
                                        borderColor: 'success.main',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <CheckCircle color="success" />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 'medium',
                                            }}
                                        >
                                            Transcription Complete
                                        </Typography>
                                        <Chip
                                            label={`${(transcription.confidence * 100).toFixed(0)}% confidence`}
                                            color={getConfidenceColor(
                                                transcription.confidence * 100,
                                            )}
                                            size="small"
                                        />
                                    </Box>
                                    <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                                        <Typography variant="body1">
                                            {transcription.text}
                                        </Typography>
                                    </Paper>
                                    {accumulatedTranscriptions.length > 1 && (
                                        <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.light' }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 'medium',
                                                    color: 'text.secondary',
                                                }}
                                            >
                                                Accumulated ({accumulatedTranscriptions.length}{' '}
                                                recordings):
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                {accumulatedTranscriptions.join(' ')}
                                            </Typography>
                                        </Paper>
                                    )}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            mt: 1,
                                            display: 'block',
                                        }}
                                    >
                                        Duration: {transcription.durationSeconds}s • ID:{' '}
                                        {transcription.transcription_uuid}
                                    </Typography>
                                </Paper>
                            )}

                            {/* Audio Playback */}
                            {recordingState.audioUrl && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="caption"
                                        gutterBottom
                                        sx={{
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Audio Preview:
                                    </Typography>
                                    <audio
                                        controls
                                        src={recordingState.audioUrl}
                                        style={{ width: '100%' }}
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════ */}
                    {/* TTS MODE (Text-to-Speech) - NEW */}
                    {/* ═══════════════════════════════════════════════════════════════ */}
                    {voiceMode === 'tts' && (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Text to Synthesize"
                                    placeholder="Enter text you want to hear spoken..."
                                    value={ttsText}
                                    onChange={(e) => setTtsText(e.target.value)}
                                    sx={{ mb: 2 }}
                                />

                                <Autocomplete
                                    options={voiceOptions}
                                    value={
                                        voiceOptions.find((v) => v.value === selectedVoice) ||
                                        voiceOptions[0]
                                    }
                                    onChange={(_, newValue) => {
                                        if (newValue) setSelectedVoice(newValue.value);
                                    }}
                                    getOptionLabel={(option) => option.label}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Voice" size="small" />
                                    )}
                                    sx={{ mb: 2 }}
                                />

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    startIcon={
                                        ttsLoading ? (
                                            <CircularProgress size={20} color="inherit" />
                                        ) : (
                                            <PlayArrow />
                                        )
                                    }
                                    onClick={generateSpeech}
                                    disabled={ttsLoading || !ttsText.trim()}
                                >
                                    {ttsLoading ? 'Generating Speech...' : 'Generate Speech'}
                                </Button>
                            </Box>

                            {/* TTS Error */}
                            {ttsError && (
                                <Paper
                                    sx={{
                                        p: 2,
                                        mb: 2,
                                        bgcolor: 'error.light',
                                        color: 'error.contrastText',
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Warning />
                                        <Typography variant="body2">{ttsError}</Typography>
                                    </Box>
                                </Paper>
                            )}

                            {/* TTS Audio Player */}
                            {ttsAudioUrl && (
                                <Paper
                                    sx={{
                                        p: 2,
                                        bgcolor: 'success.light',
                                        border: 2,
                                        borderColor: 'success.main',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <CheckCircle color="success" />
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 'medium',
                                            }}
                                        >
                                            Speech Generated
                                        </Typography>
                                    </Box>
                                    <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
                                        <Typography
                                            variant="caption"
                                            gutterBottom
                                            sx={{
                                                color: 'text.secondary',
                                                display: 'block',
                                            }}
                                        >
                                            Audio Player:
                                        </Typography>
                                        <audio
                                            controls
                                            src={ttsAudioUrl}
                                            style={{ width: '100%' }}
                                            autoPlay
                                        />
                                    </Paper>
                                </Paper>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={recordingState.isRecording}>
                        Close
                    </Button>
                    {voiceMode === 'stt' && transcription && (
                        <Button
                            variant="contained"
                            startIcon={<Send />}
                            onClick={() => {
                                // Reset recording state and accumulated transcriptions
                                resetRecording();
                                setAccumulatedTranscriptions([]);
                                setBusinessSubject('');
                                setSubjectTransaction('');
                                setSelectedDataUses([]);
                                setSelectedAgenticFunction(null);
                                setOpen(false);
                            }}
                        >
                            Use Transcription
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

Self.displayName = getTsxFileName(import.meta.url);
export default Self;
